/* Notification Center → Email Templates → Insert Field → Manage Fields (RTCFR 2026-10-19).
 *
 * The administrator's CRUD over dbo.AI_NotificationTemplateField: a field is a placeholder
 * ({{Code}}) mapped to ONE Rule Category, read either from an approved Source Table + Column
 * (Database) or computed by the alert itself (Alert). The server validates everything
 * (approved table, live column, code unique within the category, 24-hour date formats);
 * the form only mirrors those rules for the user. Saves / deletes / (de)activations go
 * through the shared useSaveFlow / useDeleteFlow, so each is confirmed, audited and
 * acknowledged like every other form. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../components/AppDialog';
import { AppToolbar } from '../../components/AppToolbar';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { ComboSelect } from '../../components/ComboSelect';
import { useDeleteFlow, useSaveFlow } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import {
  createTemplateField, deleteTemplateField, getTemplateFieldOptions, listSourceColumns, listSourceTables, listTemplateFields,
  templateFieldRefusal, toFieldInput, updateTemplateField,
  type AlertFieldOption, type SourceColumn, type SourceTable, type TemplateField, type TemplateFieldDataType, type TemplateFieldInput,
} from './templateFieldsApi';

/** The audit FormName (CLAUDE.md §14b.2) — Settings → Audit Trail Configuration drives the reason gate. */
export const TEMPLATE_FIELDS_AUDIT_FORM = 'Notification Template Fields';
const DATA_TYPES: TemplateFieldDataType[] = ['Text', 'Number', 'Date', 'DateTime', 'Boolean'];
const CODE_RE = /^\{\{\s*[A-Za-z][A-Za-z0-9]*\s*\}\}$|^[A-Za-z][A-Za-z0-9]*$/;

export interface FieldCategory { code: number; name: string }

const sourceLabel = (f: TemplateField) =>
  f.sourceTable ? `${f.sourceTable}.${f.sourceColumn}` : 'Computed by the alert';

/** Manage Fields — the field list (all categories, or one) with New / Edit / Delete / Activate. */
export function TemplateFieldsManager({ categories, initialCategoryCode, onClose, onChanged }: {
  categories: FieldCategory[];
  initialCategoryCode?: number;
  onClose: () => void;
  /** Called after any change so the editor's Insert Field panel reloads. */
  onChanged: () => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const [filterCode, setFilterCode] = useState<number>(initialCategoryCode ?? 0);
  const [rows, setRows] = useState<TemplateField[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [form, setForm] = useState<{ field?: TemplateField } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listTemplateFields(filterCode || undefined, true);
      setRows(list);
      setSelected((sel) => new Set([...sel].filter((id) => list.some((r) => r.id === id))));
    } catch {
      notify.error('Could not load the template fields. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [filterCode, notify]);
  useEffect(() => { void load(); }, [load]);

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);
  const single = selectedRows.length === 1 ? selectedRows[0] : undefined;

  function setActive(active: boolean) {
    const targets = selectedRows.filter((r) => r.isActive !== active);
    if (targets.length === 0) return;
    const verb = active ? 'Activate' : 'Deactivate';
    void runSave({
      formName: TEMPLATE_FIELDS_AUDIT_FORM, auditAction: 'edit',
      confirmTitle: verb,
      confirmMessage: active
        ? `Activate ${targets.length} field(s)? They are offered again in Insert Field.`
        : `Deactivate ${targets.length} field(s)? They are no longer offered in Insert Field; templates that already use them still fill them in.`,
      successMessage: `${verb}d successfully.`,
      save: async () => { for (const r of targets) await updateTemplateField(r.id, { ...toFieldInput(r), isActive: active }); },
      onSaved: () => { void load(); onChanged(); },
      onError: (e) => notify.error(templateFieldRefusal(e) ?? `The field(s) could not be ${verb.toLowerCase()}d.`),
    });
  }

  function remove() {
    if (selectedRows.length === 0) return;
    const targets = selectedRows;
    void runDelete({
      formName: TEMPLATE_FIELDS_AUDIT_FORM,
      message: targets.length === 1
        ? `Delete the field ${targets[0]!.fieldCode} (${targets[0]!.fieldName})? Email templates that use it are not changed — the placeholder will be sent as written.`
        : `Delete ${targets.length} fields? Email templates that use them are not changed — their placeholders will be sent as written.`,
      delete: async () => { for (const r of targets) await deleteTemplateField(r.id); },
      onDeleted: () => { setSelected(new Set()); void load(); onChanged(); },
      onError: (e) => notify.error(templateFieldRefusal(e) ?? 'The field could not be deleted.'),
    });
  }

  const columns: AppColumn<TemplateField>[] = [
    { field: 'fieldName', header: 'Field Name', minWidth: 150 },
    { field: 'fieldCode', header: 'Field Code', minWidth: 150 },
    { field: 'categoryName', header: 'Category', minWidth: 140 },
    { field: 'resolverType', header: 'Resolver', width: 100 },
    { field: 'source', header: 'Data Source', minWidth: 190, text: sourceLabel, render: sourceLabel },
    { field: 'dataType', header: 'Data Type', width: 100 },
    { field: 'format', header: 'Format', width: 110, text: (r) => r.format ?? '', render: (r) => r.format ?? '' },
    { field: 'displayOrder', header: 'Order', width: 80, align: 'right' },
    { field: 'isActive', header: 'Active', width: 90, type: 'status', text: (r) => (r.isActive ? 'Active' : 'Inactive') },
  ];
  const exp = gridExportMatrix(rows, columns);

  return (
    <>
      <AppDialog open width={1100} maximizable icon="ti-forms" title="Manage Insert Fields" onClose={onClose}
        footer={<button type="button" className="ca-btn" onClick={onClose}>Close</button>}>
        <div className="tf-bar">
          <label className="tf-filter">
            <span>Category</span>
            <select className="nc-fselect" value={filterCode} aria-label="Category filter"
              data-unsaved-ignore onChange={(e) => setFilterCode(Number(e.target.value))}>
              <option value={0}>All categories</option>
              {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </label>
          <AppToolbar
            left={(
              <>
                <button type="button" className="ca-btn" disabled={!selectedRows.some((r) => !r.isActive)} onClick={() => setActive(true)}>
                  <i className="ti ti-circle-check" /> Activate
                </button>
                <button type="button" className="ca-btn" disabled={!selectedRows.some((r) => r.isActive)} onClick={() => setActive(false)}>
                  <i className="ti ti-circle-off" /> Deactivate
                </button>
              </>
            )}
            onNew={() => setForm({})}
            onEdit={() => single && setForm({ field: single })}
            editDisabled={!single}
            onDelete={remove}
            deleteDisabled={selectedRows.length === 0}
            onRefresh={() => void load()}
            refreshing={loading}
            exportData={{ filename: 'Email Template Fields', ...exp }}
          />
        </div>
        <AppDataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          onRowDoubleClick={(r) => setForm({ field: r })}
          emptyText="No fields for this category yet. Click New to add one."
          exportFilename="Email Template Fields"
          hideHeaderTools
        />
      </AppDialog>
      {form && (
        <TemplateFieldForm field={form.field} categories={categories}
          defaultCategoryCode={filterCode || initialCategoryCode || categories[0]?.code}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); void load(); onChanged(); }} />
      )}
    </>
  );
}

interface Draft {
  fieldName: string; fieldCode: string; categoryCode: number; resolverType: 'Database' | 'Alert';
  sourceTable: string; sourceColumn: string; dataType: TemplateFieldDataType; format: string; displayOrder: string; isActive: boolean;
}

function draftOf(f: TemplateField | undefined, categoryCode: number | undefined): Draft {
  return f
    ? { fieldName: f.fieldName, fieldCode: f.fieldCode, categoryCode: f.categoryCode, resolverType: f.resolverType,
        sourceTable: f.sourceTable ?? '', sourceColumn: f.sourceColumn ?? '', dataType: f.dataType, format: f.format ?? '',
        displayOrder: String(f.displayOrder), isActive: f.isActive }
    : { fieldName: '', fieldCode: '', categoryCode: categoryCode ?? 0, resolverType: 'Database', sourceTable: '', sourceColumn: '',
        dataType: 'Text', format: '', displayOrder: '', isActive: true };
}

/** New / Edit one field. */
export function TemplateFieldForm({ field, categories, defaultCategoryCode, onClose, onSaved }: {
  field?: TemplateField; categories: FieldCategory[]; defaultCategoryCode?: number; onClose: () => void; onSaved: () => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [d, setD] = useState<Draft>(() => draftOf(field, defaultCategoryCode));
  const [tables, setTables] = useState<SourceTable[]>([]);
  const [columns, setColumns] = useState<SourceColumn[]>([]);
  const [alertFields, setAlertFields] = useState<AlertFieldOption[]>([]);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    let live = true;
    void Promise.all([listSourceTables(), getTemplateFieldOptions()])
      .then(([t, o]) => { if (live) { setTables(t); setAlertFields(o.alertFields); } })
      .catch(() => notify.error('Could not load the approved data sources.'));
    return () => { live = false; };
  }, [notify]);

  // Selecting a Source Table refreshes the Source Column list.
  useEffect(() => {
    if (!d.sourceTable) { setColumns([]); return; }
    let live = true;
    void listSourceColumns(d.sourceTable)
      .then((c) => { if (live) setColumns(c); })
      .catch(() => { if (live) { setColumns([]); notify.error(`Could not load the columns of ${d.sourceTable}.`); } });
    return () => { live = false; };
  }, [d.sourceTable, notify]);

  const isDb = d.resolverType === 'Database';
  const codeValid = CODE_RE.test(d.fieldCode.trim());
  const orderNum = d.displayOrder.trim() === '' ? null : Number(d.displayOrder);
  const orderValid = orderNum === null || (Number.isInteger(orderNum) && orderNum >= 0 && orderNum <= 100000);
  const missing = {
    fieldName: !d.fieldName.trim(), fieldCode: !d.fieldCode.trim(), categoryCode: !d.categoryCode,
    sourceTable: isDb && !d.sourceTable, sourceColumn: isDb && !d.sourceColumn,
  };
  const req = (k: keyof typeof missing) => (missing[k] ? ' tf-empty' : '');
  const formatAllowed = d.dataType === 'Number' || d.dataType === 'Date' || d.dataType === 'DateTime';

  function pickColumn(col: string) {
    const c = columns.find((x) => x.column === col);
    setD((x) => ({ ...x, sourceColumn: col, dataType: c?.suggestedDataType ?? x.dataType, format: '' }));
  }

  function pickAlertCode(code: string) {
    const a = alertFields.find((f) => f.code === code);
    setD((x) => ({
      ...x, fieldCode: code,
      fieldName: x.fieldName.trim() ? x.fieldName : (a?.name ?? ''),
      dataType: a?.dataType ?? x.dataType,
      sourceTable: a?.sourceTable ?? '', sourceColumn: a?.sourceColumn ?? '', format: '',
    }));
  }

  function save() {
    if (Object.values(missing).some(Boolean)) { notify.error('Fill in the required fields (highlighted).'); return; }
    if (!codeValid) { notify.error('Field Code must start with a letter and contain only letters and digits, e.g. {{SampleId}}.'); return; }
    if (!orderValid) { notify.error('Display Order must be a whole number from 0 to 100000.'); return; }
    const input: TemplateFieldInput = {
      categoryCode: d.categoryCode, fieldName: d.fieldName.trim(), fieldCode: d.fieldCode.trim(), resolverType: d.resolverType,
      sourceTable: d.sourceTable || null, sourceColumn: d.sourceColumn || null, dataType: d.dataType,
      format: formatAllowed && d.format.trim() ? d.format.trim() : null, displayOrder: orderNum, isActive: d.isActive,
    };
    void runSave({
      formName: TEMPLATE_FIELDS_AUDIT_FORM, auditAction: field ? 'edit' : 'create',
      save: async () => { if (field) await updateTemplateField(field.id, input); else await createTemplateField(input); },
      onSaved,
      onError: (e) => notify.error(templateFieldRefusal(e) ?? 'The field could not be saved.'),
    });
  }

  const tableOptions = tables.map((t) => ({ value: t.table, label: t.label }));
  const columnOptions = columns.map((c) => ({ value: c.column, label: `${c.column} (${c.sqlType})` }));
  const selectedTable = tables.find((t) => t.table === d.sourceTable);

  return (
    <AppDialog open width={720} icon="ti-forms" title={field ? `Edit Field — ${field.fieldCode}` : 'New Field'} onClose={onClose}
      footer={(
        <>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={save}><span className="tb-i">💾</span> Save</button>
        </>
      )}>
      <div className="tf-form">
        <label className="tf-row"><span className="tf-lbl">Field Name <b className="tf-star">*</b></span>
          <input className={`nc-finput${req('fieldName')}`} value={d.fieldName} maxLength={100} aria-label="Field Name"
            onChange={(e) => set('fieldName', e.target.value)} placeholder="e.g. Project Name" /></label>

        <label className="tf-row"><span className="tf-lbl">Resolver</span>
          <select className="nc-fselect" value={d.resolverType} aria-label="Resolver"
            onChange={(e) => setD((x) => ({ ...x, resolverType: e.target.value as Draft['resolverType'], fieldCode: '', sourceTable: '', sourceColumn: '' }))}>
            <option value="Database">Database — read from a table column</option>
            <option value="Alert">Alert — computed by the alert</option>
          </select></label>

        <label className="tf-row"><span className="tf-lbl">Field Code <b className="tf-star">*</b></span>
          {isDb ? (
            <input className={`nc-finput tf-code${req('fieldCode')}`} value={d.fieldCode} maxLength={64} aria-label="Field Code"
              aria-invalid={!!d.fieldCode.trim() && !codeValid}
              onChange={(e) => set('fieldCode', e.target.value)} placeholder="{{ProjectName}}" />
          ) : (
            <div className={`tf-combo${req('fieldCode')}`}>
              <ComboSelect value={d.fieldCode} onChange={pickAlertCode} placeholder="Choose an alert value…"
                options={alertFields.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}` }))} clearable={false} />
            </div>
          )}</label>

        <label className="tf-row"><span className="tf-lbl">Category <b className="tf-star">*</b></span>
          <select className={`nc-fselect${req('categoryCode')}`} value={d.categoryCode} aria-label="Category"
            onChange={(e) => set('categoryCode', Number(e.target.value))}>
            <option value={0}>— Select —</option>
            {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select></label>

        <div className="tf-row"><span className="tf-lbl">Source Table {isDb && <b className="tf-star">*</b>}</span>
          <div className={`tf-combo${req('sourceTable')}`}>
            <ComboSelect value={d.sourceTable} options={tableOptions} placeholder={isDb ? 'Search tables…' : '(optional — preview only)'}
              onChange={(v) => setD((x) => ({ ...x, sourceTable: v, sourceColumn: '' }))} />
          </div></div>
        {selectedTable && <div className="tf-hint">Read from the alert's own sample test via {selectedTable.path}.</div>}

        <div className="tf-row"><span className="tf-lbl">Source Column {isDb && <b className="tf-star">*</b>}</span>
          <div className={`tf-combo${req('sourceColumn')}`}>
            <ComboSelect value={d.sourceColumn} options={columnOptions} disabled={!d.sourceTable}
              placeholder={d.sourceTable ? 'Search columns…' : 'Choose a Source Table first'} onChange={pickColumn} />
          </div></div>

        <label className="tf-row"><span className="tf-lbl">Data Type</span>
          <select className="nc-fselect" value={d.dataType} aria-label="Data Type"
            onChange={(e) => setD((x) => ({ ...x, dataType: e.target.value as TemplateFieldDataType, format: '' }))}>
            {DATA_TYPES.map((t) => <option key={t} value={t}>{t === 'DateTime' ? 'Date / Time' : t}</option>)}
          </select></label>

        <label className="tf-row"><span className="tf-lbl">Format</span>
          <input className="nc-finput" value={d.format} maxLength={50} aria-label="Format" disabled={!formatAllowed}
            onChange={(e) => set('format', e.target.value)}
            placeholder={d.dataType === 'Number' ? 'e.g. 0.00' : d.dataType === 'Date' ? 'MM/dd/yyyy (default)' : d.dataType === 'DateTime' ? 'MM/dd/yyyy HH:mm (default)' : 'Not used for this type'} /></label>

        <label className="tf-row"><span className="tf-lbl">Display Order</span>
          <input className="nc-finput tf-order" type="number" min={0} max={100000} step={1} value={d.displayOrder} aria-label="Display Order"
            aria-invalid={!orderValid} onChange={(e) => set('displayOrder', e.target.value)} placeholder="After the last field" /></label>

        <label className="tf-row"><span className="tf-lbl">Active</span>
          <span className="tf-check"><input type="checkbox" checked={d.isActive} aria-label="Active" onChange={(e) => set('isActive', e.target.checked)} />
            <span>Offered in Insert Field</span></span></label>
      </div>
    </AppDialog>
  );
}
